import { Box, Button, Collapse, Dialog, DialogContent, DialogTitle, Rating, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { safeHotelImageUrl, HotelPublicContent } from '@bandido/shared/types';
export function hotelContentDisplayName(content: HotelPublicContent | null | undefined, fallback: string) {
  return content?.status === 'published' && content.display_name?.trim() ? content.display_name : fallback;
}
export function HotelContentImage({
  url,
  name,
  index = 1,
  contain = false,
  banner = false,
}: {
  url?: string | null;
  name: string;
  index?: number;
  contain?: boolean;
  banner?: boolean;
}) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState<string | null>(null);
  const safe = safeHotelImageUrl(url);
  return (
    <Box
      sx={{
        aspectRatio: banner ? undefined : '16 / 10',
        height: banner ? { xs: 150, md: 175 } : undefined,
        maxHeight: contain ? '65vh' : 300,
        width: '100%',
        bgcolor: 'action.hover',
        overflow: 'hidden',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {safe && failed !== safe ? (
        <img
          src={safe}
          alt={t('hotelSearch.hotelPhoto', { name, index })}
          width={640}
          height={400}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setFailed(safe)}
          style={{ width: '100%', height: '100%', objectFit: contain ? 'contain' : 'cover' }}
        />
      ) : (
        <Typography color="text.secondary" role="img" aria-label={t('hotelSearch.imageUnavailable')}>
          {t('hotelSearch.imageUnavailable')}
        </Typography>
      )}
    </Box>
  );
}
export function HotelPublishedContent({
  content,
  name,
  detail = false,
  loading = false,
  card = false,
}: {
  content?: HotelPublicContent | null;
  name: string;
  detail?: boolean;
  loading?: boolean;
  card?: boolean;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const c = content?.status === 'published' ? content : null;
  const images = c?.gallery?.length ? c.gallery : c?.main_image ? [c.main_image] : [];
  const index = Math.min(selected, Math.max(0, images.length - 1));
  const descriptions = [c?.description, ...(c?.descriptions ?? [])].filter(
    (d, i, all) => d?.text.trim() && all.findIndex((x) => x?.text === d.text && x?.language === d.language) === i,
  );
  return (
    <Stack gap={1} sx={{ minWidth: 0 }}>
      {loading && (
        <Typography variant="caption" role="status">
          {t('hotelSearch.contentLoading')}
        </Typography>
      )}
      <Stack direction={detail || card ? 'column' : 'row'} gap={2} alignItems={detail || card ? undefined : 'center'}>
        <Box sx={{ width: detail || card ? '100%' : { xs: 96, sm: 132 }, flexShrink: 0, minWidth: 0 }}>
          {detail && images.length ? (
            <>
              <Button
                onClick={() => setZoom(true)}
                aria-label={t('hotelSearch.enlargePhoto')}
                sx={{ p: 0, width: '100%' }}
              >
                <HotelContentImage url={images[index]?.url} name={name} index={index + 1} contain />
              </Button>
              <Stack direction="row" gap={1} sx={{ overflowX: 'auto', py: 1, maxWidth: '100%' }}>
                {images.map((image, i) => (
                  <Button
                    key={`${image.url}:${i}`}
                    aria-label={t('hotelSearch.hotelPhoto', { name, index: i + 1 })}
                    aria-pressed={index === i}
                    onClick={() => setSelected(i)}
                    sx={{
                      p: 0,
                      minWidth: 64,
                      width: 64,
                      flexShrink: 0,
                      border: 2,
                      borderColor: index === i ? 'primary.main' : 'transparent',
                    }}
                  >
                    <HotelContentImage url={image.url} name={name} index={i + 1} />
                  </Button>
                ))}
              </Stack>
              <Dialog
                open={zoom}
                onClose={() => setZoom(false)}
                fullWidth
                maxWidth="lg"
                aria-labelledby="hotel-photo-title"
              >
                <DialogTitle id="hotel-photo-title">
                  {name}
                  <Button onClick={() => setZoom(false)}>{t('hotelSearch.closePhoto')}</Button>
                </DialogTitle>
                <DialogContent>
                  <HotelContentImage url={images[index]?.url} name={name} index={index + 1} contain />
                </DialogContent>
              </Dialog>
            </>
          ) : (
            <HotelContentImage url={c?.main_image?.url} name={name} banner={card} />
          )}
        </Box>
        <Stack gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
          {c?.category_stars != null && c.category_classification_source && (
            <Rating
              readOnly
              value={c.category_stars}
              size="small"
              aria-label={t('hotelSearch.verifiedStars', { count: c.category_stars })}
            />
          )}
          {!card && (
            <Typography variant="body2">
              {c?.location
                ? [c.location.address, c.location.city, c.location.country_code].filter(Boolean).join(' · ') ||
                  t('hotelSearch.locationUnknown')
                : t('hotelSearch.locationUnknown')}
            </Typography>
          )}
          {!c && !loading && <Typography variant="caption">{t('hotelSearch.contentMissing')}</Typography>}
          {detail && (
            <>
              <Button
                onClick={() => setDescriptionOpen(!descriptionOpen)}
                aria-expanded={descriptionOpen}
                aria-controls="hotel-description"
              >
                {t(descriptionOpen ? 'hotelSearch.hideDescription' : 'hotelSearch.showDescription')}
              </Button>
              <Collapse in={descriptionOpen} id="hotel-description">
                {descriptions.length ? (
                  descriptions.map((d, i) => (
                    <Box key={i}>
                      <Typography variant="caption">
                        {t('hotelSearch.descriptionLanguage', { language: d!.language })}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
                        {d!.text}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography>{t('hotelSearch.descriptionUnknown')}</Typography>
                )}
              </Collapse>
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
